import { LoginRounded } from "@mui/icons-material"
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Stack,
} from "@mui/material"
import { useEffect, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { ConnectorManagementButtons } from "../connectors/ConnectorManagementButtons"
import { ConnectorSelector } from "../connectors/ConnectorSelector"
import { useConnectors } from "../connectors/useConnectors"
import type { SettingsConnectorType } from "@oboku/shared"
import { CancelButton } from "../common/forms/CancelButton"

export type UploadConnectorSelectionStepProps<TAuthResult> = {
  connectorType: SettingsConnectorType
  onAuthenticated: (result: TAuthResult) => void
  onClose: () => void
  /** Returns auth result; called when user clicks Connect/Sign in */
  authenticate: (connectorId: string) => Promise<TAuthResult>
  /** Optional: initial selected connector id (e.g. from session) */
  initialConnectorId?: string
  /** When set, hides "New connector" button once this many connectors exist. */
  maxConnectors?: number
}

export function UploadConnectorSelectionStep<TAuthResult>(
  props: UploadConnectorSelectionStepProps<TAuthResult>,
) {
  const {
    connectorType,
    onAuthenticated,
    onClose,
    authenticate,
    initialConnectorId,
    maxConnectors,
  } = props

  const { data: connectors = [] } = useConnectors({ type: connectorType })
  const [selectedConnectorId, setSelectedConnectorId] = useState<
    string | undefined
  >(initialConnectorId)

  useEffect(() => {
    if (!selectedConnectorId && connectors[0]?.id) {
      setSelectedConnectorId(connectors[0].id)
    }
  }, [connectors, selectedConnectorId])

  const authenticateMutation = useMutation({
    mutationFn: authenticate,
    onSuccess: (result) => {
      onAuthenticated(result)
    },
  })

  return (
    <>
      <DialogContent>
        <Stack
          sx={{
            gap: 2,
            py: 2,
          }}
        >
          <Alert severity="info">
            Select a connector, then sign in to browse and add files.
          </Alert>
          <ConnectorSelector
            connectorType={connectorType}
            value={selectedConnectorId}
            onChange={(e) => setSelectedConnectorId(e.target.value)}
          />
          <ConnectorManagementButtons
            connectorType={connectorType}
            onNavigate={onClose}
            showNewButton={
              maxConnectors === undefined || connectors.length < maxConnectors
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <Button
          disabled={!selectedConnectorId}
          loading={authenticateMutation.isPending}
          onClick={() => {
            if (selectedConnectorId) {
              authenticateMutation.mutate(selectedConnectorId)
            }
          }}
          startIcon={<LoginRounded />}
          variant="contained"
        >
          Connect
        </Button>
      </DialogActions>
    </>
  )
}
